// Design Tokens System
// Centralized design constants for consistent styling

export const gradients = {
  primary: "from-blue-600 via-purple-600 to-pink-600",
  secondary: "from-blue-500 to-purple-500", 
  accent: "from-purple-500 to-pink-500",
  success: "from-green-500 to-emerald-500",
  warning: "from-yellow-500 to-orange-500",
  danger: "from-red-500 to-pink-500",
  // Background gradients
  backgroundLight: "from-blue-50/50 to-purple-50/50",
  backgroundMedium: "from-blue-100/50 to-purple-100/50",
  backgroundDark: "from-blue-900/50 to-purple-900/50",
} as const;

export const shadows = {
  glow: "shadow-lg shadow-blue-500/25",
  glowPurple: "shadow-lg shadow-purple-500/25",
  glowPink: "shadow-lg shadow-pink-500/25",
  strong: "shadow-2xl shadow-black/10",
  card: "shadow-lg shadow-black/5",
  hover: "shadow-xl shadow-black/10",
} as const;

export const animations = {
  durations: {
    fast: 0.2,
    normal: 0.4,
    slow: 0.6,
    slower: 0.8,
  },
  delays: {
    none: 0,
    short: 0.1,
    medium: 0.2,
    long: 0.4,
  },
  easings: {
    smooth: [0.4, 0.0, 0.2, 1] as const,
    bounce: [0.68, -0.55, 0.265, 1.55] as const,
    sharp: [0.4, 0.0, 0.6, 1] as const,
  },
} as const;

export const spacing = {
  sections: {
    small: "py-12",
    medium: "py-16", 
    large: "py-20",
    xlarge: "py-24",
  },
  containers: {
    small: "max-w-4xl",
    medium: "max-w-5xl",
    large: "max-w-6xl",
    xlarge: "max-w-7xl",
    full: "max-w-full",
  },
  gaps: {
    small: "gap-4",
    medium: "gap-6",
    large: "gap-8",
    xlarge: "gap-12",
  },
} as const;

export const colors = {
  feature: {
    blue: { bg: "bg-blue-100", text: "text-blue-500", border: "border-blue-200" },
    green: { bg: "bg-green-100", text: "text-green-500", border: "border-green-200" },
    purple: { bg: "bg-purple-100", text: "text-purple-500", border: "border-purple-200" },
    yellow: { bg: "bg-yellow-100", text: "text-yellow-500", border: "border-yellow-200" },
    red: { bg: "bg-red-100", text: "text-red-500", border: "border-red-200" },
    indigo: { bg: "bg-indigo-100", text: "text-indigo-500", border: "border-indigo-200" },
    pink: { bg: "bg-pink-100", text: "text-pink-500", border: "border-pink-200" },
    orange: { bg: "bg-orange-100", text: "text-orange-500", border: "border-orange-200" },
  },
} as const;

// Utility function to get consistent gradient classes
export const getGradient = (type: keyof typeof gradients) => `bg-gradient-to-r ${gradients[type]}`;

// Utility function to get feature color scheme
export const getFeatureColors = (colorName: string) => {
  // Map the color names from the existing system to our tokens
  const colorMap = {
    'text-blue-500': colors.feature.blue,
    'text-green-500': colors.feature.green,
    'text-purple-500': colors.feature.purple,
    'text-yellow-500': colors.feature.yellow,
    'text-red-500': colors.feature.red,
    'text-indigo-500': colors.feature.indigo,
    'text-pink-500': colors.feature.pink,
    'text-orange-500': colors.feature.orange,
  };
  
  return colorMap[colorName as keyof typeof colorMap] || colors.feature.blue;
};
